import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';

import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { motion } from 'framer-motion';

import Loading from '../components/global/loading/Loading';
import ProductCard from '../components/ProductCard';
import { useProducer } from '../context/ProducerContext';
import { useProduct } from '../context/ProductContext';
import { Producer } from '../models/Producer';
import { Product } from '../models/Product';
import Button from '../components/formElements/Button';
import { UserRole } from '../models/User';
import {
  Edit,
  Business,
  WebAsset,
  LocationOn,
  DateRange,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../utils/useConfig';
import Seo from '../components/seo/Seo';

const fadeIn = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 },
  },
};

const staggerChildren = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

function ProducerPage() {
  const config = useConfig();

  const { name } = useParams<{ name: string }>();

  const [producer, setProducer] = React.useState<Producer | null>(null);
  const { getProducerByName } = useProducer();

  const [products, setProducts] = React.useState<Product[]>([]);
  const { getProductsForProducer } = useProduct();

  const [loading, setLoading] = React.useState(true);

  const { user } = useAuth();

  const navigate = useNavigate();

  useEffect(() => {
    if (!name) {
      toast.error('Nie znaleziono producenta');
      navigate('/');
    }

    async function fetchProducer() {
      try {
        const response = await getProducerByName(name as string);
        setProducer(response);
      } catch (e) {
        toast.error('Nie udało się pobrać producenta');
        navigate('/');
        console.error(e);
      }
    }

    async function fetchProducts() {
      try {
        const response = await getProductsForProducer(name as string);
        setProducts(response);
      } catch (e) {
        toast.error('Nie udało się pobrać produktów producenta');
        console.error(e);
      } finally {
        setLoading(false);
      }
    }

    fetchProducer();
    fetchProducts();
  }, [getProducerByName, getProductsForProducer, name, navigate]);

  if (loading || !producer) return <Loading />;

  const hasAdditionalInfo =
    producer.history ||
    producer.foundedYear ||
    producer.headquarters ||
    producer.website ||
    producer.speciality;

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Hero Section with SVG Background */}
        <div className="relative overflow-hidden bg-gradient-to-r from-primary to-primary-dark text-white py-16">
          <div className="absolute inset-0 overflow-hidden">
            <svg
              className="absolute bottom-0 left-0 w-full h-64 text-white opacity-10"
              viewBox="0 0 1200 120"
              preserveAspectRatio="none"
            >
              <path
                d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
                fill="currentColor"
              ></path>
              <path
                d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z"
                opacity=".5"
                fill="currentColor"
              ></path>
              <path
                d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"
                opacity=".25"
                fill="currentColor"
              ></path>
            </svg>
          </div>

          <motion.div
            className="container mx-auto px-4 md:px-8 relative z-10"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
              <motion.div
                className="bg-white rounded-lg shadow-2xl p-6 flex items-center justify-center md:w-56 h-56 transform perspective-1000"
                initial={{ opacity: 0, rotateY: -30 }}
                animate={{ opacity: 1, rotateY: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {producer.image ? (
                  <img
                    src={producer.image}
                    alt={producer.name}
                    className="object-contain max-h-44 max-w-44"
                  />
                ) : (
                  <div className="bg-gradient-to-br from-primary-light to-primary rounded-lg h-44 w-44 flex items-center justify-center text-white text-4xl font-bold">
                    {producer.name.charAt(0)}
                  </div>
                )}
              </motion.div>
              <motion.div
                className="flex-1 text-center md:text-left"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-200">
                  {producer.name}
                </h1>
                <p className="text-sm sm:text-lg md:text-xl opacity-90 max-w-2xl">
                  {producer.description ||
                    `Odkryj produkty marki ${producer.name} w ofercie ${config.company.name}`}
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {user?.role === UserRole.Admin && (
          <motion.div
            className="container mx-auto px-4 md:px-8 -mt-6 mb-6 relative z-20"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="p-4 rounded-lg bg-white shadow-md border-l-4 border-primary">
              <h2 className="text-lg font-medium mb-2">
                Akcje administracyjne
              </h2>
              <div className="flex space-x-2 items-end">
                <Button
                  colorTheme="black"
                  className="w-fit hover:shadow-lg transition-all duration-300"
                  size="xs"
                  onClick={() => {
                    navigate(`/admin/producers/edit/${producer.id}`);
                  }}
                  aria-label="edit-product"
                >
                  Edytuj producenta
                  <Edit
                    sx={{
                      fontSize: window.innerWidth <= 768 ? 10 : 25,
                      marginLeft: 1,
                    }}
                    color="secondary"
                  />
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Informacje o firmie */}
        <motion.div
          className="container mx-auto px-4 md:px-8 py-8"
          variants={staggerChildren}
          initial="initial"
          animate="animate"
        >
          <motion.div
            className="bg-white rounded-2xl shadow-lg p-8 mb-12 relative overflow-hidden"
            variants={fadeIn}
          >
            {/* Subtle background pattern */}
            <div className="absolute top-0 right-0 w-40 h-40 opacity-10">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
                <defs>
                  <pattern
                    id="grid"
                    width="10"
                    height="10"
                    patternUnits="userSpaceOnUse"
                  >
                    <path
                      d="M 10 0 L 0 0 0 10"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="0.5"
                    />
                  </pattern>
                </defs>
                <rect width="100" height="100" fill="url(#grid)" />
              </svg>
            </div>

            <motion.h2
              className="text-2xl md:text-3xl font-bold mb-6 pb-2 border-b border-gray-200 relative inline-block"
              variants={fadeIn}
            >
              O firmie {producer.name}
              <span className="absolute bottom-0 left-0 w-full h-1 bg-primary rounded-full"></span>
            </motion.h2>

            {hasAdditionalInfo ? (
              <div className="space-y-6 relative z-10">
                {producer.history && (
                  <motion.p
                    className="text-gray-700 text-lg leading-relaxed"
                    variants={fadeIn}
                  >
                    {producer.history}
                  </motion.p>
                )}

                <motion.div
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-6"
                  variants={staggerChildren}
                >
                  {producer.foundedYear && (
                    <motion.div
                      className="bg-gradient-to-br from-gray-50 to-gray-100 p-5 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-300"
                      variants={fadeIn}
                      whileHover={{ y: -5, transition: { duration: 0.2 } }}
                    >
                      <div className="flex items-center mb-3">
                        <div className="bg-primary/10 p-2 rounded-full mr-3">
                          <DateRange className="text-primary" />
                        </div>
                        <h3 className="text-sm text-gray-500 uppercase font-semibold">
                          Rok założenia
                        </h3>
                      </div>
                      <p className="text-xl font-medium text-gray-800">
                        {producer.foundedYear}
                      </p>
                    </motion.div>
                  )}

                  {producer.headquarters && (
                    <motion.div
                      className="bg-gradient-to-br from-gray-50 to-gray-100 p-5 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-300"
                      variants={fadeIn}
                      whileHover={{ y: -5, transition: { duration: 0.2 } }}
                    >
                      <div className="flex items-center mb-3">
                        <div className="bg-primary/10 p-2 rounded-full mr-3">
                          <LocationOn className="text-primary" />
                        </div>
                        <h3 className="text-sm text-gray-500 uppercase font-semibold">
                          Siedziba
                        </h3>
                      </div>
                      <p className="text-xl font-medium text-gray-800">
                        {producer.headquarters}
                      </p>
                    </motion.div>
                  )}

                  {producer.speciality && (
                    <motion.div
                      className="bg-gradient-to-br from-gray-50 to-gray-100 p-5 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-300"
                      variants={fadeIn}
                      whileHover={{ y: -5, transition: { duration: 0.2 } }}
                    >
                      <div className="flex items-center mb-3">
                        <div className="bg-primary/10 p-2 rounded-full mr-3">
                          <Business className="text-primary" />
                        </div>
                        <h3 className="text-sm text-gray-500 uppercase font-semibold">
                          Specjalizacja
                        </h3>
                      </div>
                      <p className="text-xl font-medium text-gray-800">
                        {producer.speciality}
                      </p>
                    </motion.div>
                  )}

                  {producer.website && (
                    <motion.div
                      className="bg-gradient-to-br from-gray-50 to-gray-100 p-5 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-300"
                      variants={fadeIn}
                      whileHover={{ y: -5, transition: { duration: 0.2 } }}
                    >
                      <div className="flex items-center mb-3">
                        <div className="bg-primary/10 p-2 rounded-full mr-3">
                          <WebAsset className="text-primary" />
                        </div>
                        <h3 className="text-sm text-gray-500 uppercase font-semibold">
                          Strona WWW
                        </h3>
                      </div>
                      <a
                        href={
                          producer.website.startsWith('http')
                            ? producer.website
                            : `https://${producer.website}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xl font-medium text-primary hover:text-primary-dark hover:underline transition-colors"
                      >
                        {producer.website.replace(/^https?:\/\//i, '')}
                      </a>
                    </motion.div>
                  )}
                </motion.div>
              </div>
            ) : (
              <motion.p className="text-gray-600 text-lg" variants={fadeIn}>
                Szczegółowe informacje o tym producencie wkrótce zostaną dodane.
                Tymczasem zapraszamy do zapoznania się z produktami tej marki
                dostępnymi w naszej ofercie.
              </motion.p>
            )}
          </motion.div>

          {/* Produkty */}
          <motion.div
            className="bg-white rounded-2xl shadow-lg p-8 relative overflow-hidden"
            variants={fadeIn}
          >
            {/* Decorative background elements */}
            <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-primary opacity-5 rounded-full"></div>
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-secondary opacity-5 rounded-full"></div>
            <div className="absolute bottom-10 right-10 w-20 h-20 bg-primary opacity-5 rounded-full"></div>

            <motion.div className="mb-10 relative z-10" variants={fadeIn}>
              <h2 className="text-2xl md:text-3xl font-bold mb-3 text-gray-800 relative inline-block">
                Produkty marki {producer.name}
                <span className="absolute -bottom-1 left-0 w-3/4 h-1 bg-primary rounded-full"></span>
              </h2>
              {products.length > 0 && (
                <p className="text-gray-600">
                  Poniżej prezentujemy wybrane produkty marki {producer.name}{' '}
                  dostępne w naszej ofercie.
                </p>
              )}
            </motion.div>

            {products.length === 0 ? (
              <motion.div
                className="bg-gray-50 rounded-xl p-10 text-center border border-gray-100"
                variants={fadeIn}
              >
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <svg
                    className="mx-auto h-16 w-16 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M9 16h6M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <h3 className="mt-4 text-xl font-medium text-gray-700">
                    Brak dostępnych produktów
                  </h3>
                  <p className="mt-2 text-gray-500 max-w-md mx-auto">
                    W tym momencie nie posiadamy produktów tego producenta.
                    Zachęcamy do sprawdzenia oferty innych producentów lub
                    skontaktowania się z nami w celu zamówienia produktów tej
                    marki.
                  </p>
                </motion.div>
              </motion.div>
            ) : (
              <motion.div variants={fadeIn}>
                <Swiper
                  slidesPerView={1}
                  spaceBetween={20}
                  grabCursor={true}
                  autoplay={{
                    delay: 3000,
                    disableOnInteraction: false,
                  }}
                  pagination={{
                    clickable: true,
                    dynamicBullets: true,
                  }}
                  navigation={true}
                  loop={products.length > 3}
                  modules={[Autoplay, Pagination, Navigation]}
                  className="mb-8 product-swiper"
                  breakpoints={{
                    640: {
                      slidesPerView: 2,
                    },
                    768: {
                      slidesPerView: 3,
                    },
                    1024: {
                      slidesPerView: 4,
                    },
                  }}
                >
                  {products.map((product) => (
                    <SwiperSlide key={product.id}>
                      <motion.div
                        className="h-full py-4"
                        whileHover={{ y: -5, transition: { duration: 0.2 } }}
                      >
                        <ProductCard product={product} vertical={true} />
                      </motion.div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      </div>
      <Seo
        title={`${producer.name}`}
        description={producer.description || ''}
        image={producer.image || config.company.logo}
        noindex={false}
      />
    </>
  );
}

export default ProducerPage;
